import { z } from "zod";
import { SolanaRpcTypes } from "../solana/rpc-types";
import { Solana } from "../solana/solana-base";
import { Base58Z, IsoDate } from "../util/data-types";
import { SolanaTransaction } from "../util/solana-transaction";
import { BaseTable, TableTimestamps } from "./schema-utils";
import { NftTypes, TokenProgram } from "./SolanaTokenProgram";
import { UserSocialLink, UserSocialLinkZ } from "./UserSchema";

export const NftInfoZ = z.object({
  api_id: z.string(), // Needed for paginating requests
  type: z.literal("nft"),
  standard: z.enum(["metaplex", "nftoken"]),

  mint_address: Solana.AddressZ,
  network: z.nativeEnum(Solana.Network),

  name: z.string(),
  uri: z.string(), // This is the URL for the Metadata

  // Offchain Metadata
  animation_url: z.string().nullable(),
  collection_name: z.string().nullable(),
  description: z.string().nullable(),
  external_url: z.string().nullable(),
  files: z
    .array(z.object({ uri: z.string(), type: z.string().optional() }))
    .nullable(),
  image: z.string().nullable(),
  traits: NftTypes.NftTraitsZ.nullable(),

  // Metaplex only
  solana_nft_collection_api_id: z.string().nullable(),
  creators: z.array(TokenProgram.CreatorZ),
  symbol: z.string(),
  supply: z.string(),
  decimals: z.number(),
  price: Solana.SolAmountZ.nullable(),
  marketplace: Solana.MarketplaceZ.nullable(),

  // Nftoken Only
  collection_address: Solana.AddressZ.nullable(),
  holder: Solana.AddressZ.nullable(),
  authority: Solana.AddressZ.nullable(),
  authority_can_update: z.boolean().nullable(),
  minted_at: z.string().nullable(),
  delegate: Solana.AddressZ.nullable(),
  is_frozen: z.boolean(),
  $kind: z.string().nullable(),

  special_type: z.enum(["glow-id-genesis"]).nullable(),
});
export type NftInfo = z.infer<typeof NftInfoZ>;

export namespace SolanaBalance {
  export const MarketplaceBalanceZ = z.object({
    type: z.literal("marketplace-balance"),
    marketplace: Solana.MarketplaceZ,

    name: z.string(), // Display name of the balance
    balance: Solana.SolAmountZ,

    manage_url: z.string().nullable(),

    can_withdraw: z.boolean(),
    can_deposit: z.boolean(),
  });

  export type MarketplaceBalance = z.infer<typeof MarketplaceBalanceZ>;
}

export type DbExchangeRate = {
  coin_id: string;
  quoted_at: string;

  price_usd: string;
  market_cap_usd: string;
  volume_24h_usd: string;
} & BaseTable;

/**
 * We want to periodically populate an NFT collection with all of the NFTs that we can find from the
 * blockchain. This process can be very slow and we don't want to populate the same NFT collection
 * with two jobs. So we use this table to create a job queue.
 *
 * Previously we were doing `SELECT ... FOR UPDATE SKIP LOCKED` on the collection table itself,
 * but this caused problems with blocking other updates due to the table being locked for the
 * duration of task (could take 20+ minutes).
 */
export type DbJobPopulateNftCollection = {
  solana_nft_collection_api_id: DbSolanaNftCollection["api_id"];
  created_at: string;
  updated_at: string;
};

export type DbSolanaNftCollection = {
  name: string;
  description: string | null;
  image_url: string | null;
  magic_eden_symbol: string;

  opensea_id: string | null;

  volume_1d_sol: string | null;
  volume_7d_sol: string | null;
  volume_30d_sol: string | null;
  volume_all_time_sol: string | null;

  avg_price_1d_sol: string | null;
  avg_price_7d_sol: string | null;
  avg_price_30d_sol: string | null;

  launched_at: string | null;

  creator_addresses: string[];
  social_links: UserSocialLink[];
  featured_nft_addresses: Solana.Address[];

  solanart_symbol: string | null;

  // Fetching NFTs is pretty slow, so we space out fetching NFTs for a collection
  nfts_last_fetched_at: string | null;

  // This is updated when we populate nft collections
  num_nfts: number | null;

  featured_position: string | null;

  metaplex_collection_address: DbSolanaToken["metaplex_collection_address"];

  flagged_at: string | null;

  traits_summary: NftCollection.TraitsSummary | null;
} & BaseTable;

export type DbSolanaToken = {
  mint_address: Solana.Address; // Primary Key

  type: "coin" | "nft";
  network: Solana.Network;

  // Mint Account
  decimals: number;
  supply: string;
  freeze_authority: Solana.Address | null;

  // Metaplex Metadata
  name: string;
  symbol: string;
  seller_fee_basis_points: string | number | null;
  creators: TokenProgram.Creator[];
  metadata_update_authority: Solana.Address | null;
  primary_sale_happened: boolean;
  metadata_is_mutable: boolean;
  metaplex_metadata_address: Solana.Address | null;
  metaplex_collection_address: Solana.Address | null;
  metaplex_collection_verified: boolean | null;
  uri: string;
  creator_address: Solana.Address | null; // This is the Metaplex candy machine usually

  flagged_at: IsoDate | null;

  solana_nft_collection_api_id: string | null;
  nft_rarity_score: string | null;
  item_id_decimal: string | null;
} & BaseTable;

export namespace SolanaNftMetadata {
  export enum FetchStatus {
    Success = "success",
    FailedTimeout = "failed_timeout",
    FailedError = "failed_error",
    FailedInvalidFormat = "failed_invalid_format",
    FailedServerError = "failed_server_error",
    FailedUnknown = "failed_unknown",
  }
}

export type DbSolanaNftMetadata = {
  url: string;
  last_fetch_status: SolanaNftMetadata.FetchStatus;
  last_fetched_at: string;

  animation_url: string | null;
  collection_name: string | null;
  description: string | null;
  name: string | null;
  external_url: string | null;
  files: TokenProgram.NftMetadata["files"] | null;
  image: string | null;
  traits: NftTypes.NftTrait[] | null;

  created_at: string;
  updated_at: string;
};

export type DbSolanaCoinMetadata = {
  mint_address: Solana.Address;

  coin_market_cap_symbol: string | null;
  coin_market_cap_id: number | null;
  coin_gecko_id: string | null;
  description: string | null;
  icon_url: string | null;
  social_links: UserSocialLink[];

  // Market data - refreshed every X minutes
  market_cap_usd: string | null;
  price_percent_change_24h: string | null;
  price_usd: string | null;
  volume_24h_usd: string | null;

  // Refreshed every X minutes by pulling swap options from Jupiter
  is_swappable: boolean;

  created_at: string;
  updated_at: string;
};

export namespace NftCollection {
  export const TraitSummaryZ = z.object({
    trait_type: z.string(),
    values: z.array(
      z.object({
        value: NftTypes.NftTraitValueZ,
        count: z.number(),
        probability: z.number(),
        rarity: z.number(),
        score: z.number(),
      }),
    ),
  });
  export type TraitSummary = z.infer<typeof TraitSummaryZ>;

  export const TraitsSummaryZ = z.array(TraitSummaryZ);
  export type TraitsSummary = z.infer<typeof TraitsSummaryZ>;

  export const BasicInfoZ = z.object({
    api_id: Base58Z, // Used for pagination
    avg_price_1d_sol: z.string().nullable(),
    avg_price_30d_sol: z.string().nullable(),
    description: z.string().nullable(),
    featured_position: z.string().nullable(),
    image_url: z.string().nullable(),
    magic_eden_symbol: z.string(),
    traits_summary: TraitsSummaryZ.nullable(),
    name: z.string(),
    network: z.nativeEnum(Solana.Network),
    social_links: z.array(UserSocialLinkZ),
    volume_1d_sol: z.string().nullable(),
    volume_7d_sol: z.string().nullable(),
    volume_30d_sol: z.string().nullable(),
    num_nfts: z.number(),
    num_nfts_for_sale: z.number().optional(),
    floor_price: Solana.SolAmountZ.nullable(),
    floor_price_sol: z.string().nullable(),
  });
  export type BasicInfo = z.infer<typeof BasicInfoZ>;

  export const InfoZ = BasicInfoZ;
  export type Info = z.infer<typeof InfoZ>;
}

export type DbSolanaBlock = {
  block_number: number;
  block_time: string;
  network: Solana.Network;
  transactions_processed: boolean;
  transactions_processed_at: IsoDate | null;
  stake_rewards_processed_at: IsoDate | null;
} & BaseTable;

export type DbSolanaTransaction = {
  signature: string;
  network: Solana.Network;

  transaction_base64: string;

  block_number: number;
  block_time: string;

  meta: SolanaRpcTypes.TransactionRawMeta | null;

  created_at: string;
};

export type DbSolanaTransactionAccount = {
  transaction_signature: string;
  address: Solana.Address;

  network: Solana.Network;

  created_at: string;
  block_number: number;
};

export type DbSolanaNftTransaction = {
  signature: string;

  amount_lamports: string;
  nft_mint_address: DbSolanaToken["mint_address"];

  solana_nft_collection_api_id: DbSolanaNftCollection["api_id"] | null;

  buyer: string | null;
  seller: string | null;

  marketplace: Solana.Marketplace;
  marketplace_program: (typeof Solana.Program.MARKETPLACE_PROGRAMS)[number];

  transaction_type: SolanaTransaction.Type;
  block_time: string;

  created_at: string;
  updated_at: string;
};

export type DbSolanaSimulatedTransaction = {
  transaction_hex: string;
  network: Solana.Network;
  status: "success" | "failed";
  requester: Solana.Address | null;
  program_addresses: Solana.Address[];
  error_info: {
    message: string;
    code: Solana.TransactionError;
    logs: string[] | null;
  } | null;
  source_url: string | null;
} & BaseTable;

export type DbSolanaStakingReward = {
  stake_address: Solana.Address;
  block_number: number;
  block_time: string;
  created_at: string;
  epoch: number;
  network: Solana.Network;
  reward_lamports: string;
  wallet_address: Solana.Address | null;
};

export type DbSolanaValidator = {
  vote_address: Solana.Address;
  network: Solana.Network;
  name: string | null;
  avatar_url: string | null;
} & BaseTable;

// If we have a swap route from coin A to coin B, we store two rows — one row with 1 = B and another
// row with 2 = B.
export type DbSolanaSwapRoute = {
  mint_address_1: Solana.Address;
  mint_address_2: Solana.Address;
} & TableTimestamps;
